/**
 * Sprint 12+I (04.07.2026) — Pionier-Cockpit V2.
 *
 * Aenderungen gegenueber V1 (Sprint 12+G):
 *  - Quick-Check-CTA linkt jetzt auf `/pionier-quick-check` (nicht `/self-test`)
 *  - CTA: "Finde deinen Fokustyp · 2 Min" + Mini-Framing
 *  - Fokustyp-Result: volles Profil (Erkennungsmerkmale + Staerke + Schatten)
 *  - Feedback-Sektion entfernt → floating `PionierFeedbackIcon`
 *  - "Andreas liest mit"-Text raus
 *  - Neue Sektion 4: "Blick in die Zukunft" mit 4 Feature-Cards
 */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { FOCUS_TYPES } from "@/lib/focusTypes";
import { FOKUSTYP_PROFILES } from "@/lib/fokustypProfiles";
import { FOKUSTYP_GLYPHS } from "@/lib/quickCheckV4";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import CockpitTour from "@/components/CockpitTour";
import PionierFeedbackIcon from "@/components/PionierFeedbackIcon";
import ShareFokustypButton from "@/components/ShareFokustypButton";
import MusterCard from "@/components/dashboard/MusterCard";
import FokustypReferenzGrid from "@/components/dashboard/FokustypReferenzGrid";
import {
  CheckCircle2,
  Lock,
  ArrowRight,
  BookOpen,
  Sparkles,
  Loader2,
  Plus,
  Infinity as InfinityIcon,
  MessageSquare,
  Send,
  Clock,
  Eye,
} from "lucide-react";

const PREVIEW_UNLOCKED_MAX = 4;

const SLUG_DE_TO_EN = {
  biene: "bee",
  eule: "owl",
  katze: "cat",
  hund: "dog",
};

// Sprint 12+M5.4a (18.07.2026) — Rueck-Mapping fuer FOKUSTYP_GLYPHS (PNG-Files
// im /public/icons/-Ordner sind auf deutschen Slugs indexiert).
const SLUG_EN_TO_DE = {
  bee: "biene",
  owl: "eule",
  cat: "katze",
  dog: "hund",
};

function normalizeFokustypSlug(raw) {
  if (!raw) return "";
  const lower = String(raw).toLowerCase();
  return SLUG_DE_TO_EN[lower] || lower;
}

// Sprint 12+M5.6c (19.07.2026) — Bald-verfügbar-Bullet-Liste ersetzt die
// bisherigen 4 Feature-Cards (FUTURE_FEATURES). Scannbare Bullet-Liste mit
// klarem Versprechen-Charakter statt visuell schwerer Cards.
// Sprint 12+M5.9-b (19.07.2026) — Andreas' Vage-Wording: Pioniere erhalten
// diese Funktionen nicht zwangsläufig alle (manche erst beim Free-Übergang),
// daher weichere Formulierungen ohne Zahlen-Versprechen.
const BALD_VERFUEGBAR_ITEMS = [
  "Vollständige Bibliothek mit vielen Bausteinen",
  "Pattern Recognition: Erkenne Muster in Echtzeit",
  "Persönliche, individuelle Situationsanalyse",
  "Schritt-für-Schritt-Gesprächsleitfäden",
];

export default function PionierCockpit() {
  const { user } = useAuth();

  const fokustyp = normalizeFokustypSlug(
    user?.primary_fokustyp || user?.fokustyp_primary || ""
  );
  const firstName = (user?.first_name || user?.name || "").split(" ")[0] || "";
  const hasFokustyp = !!FOKUSTYP_PROFILES[fokustyp];

  return (
    <div
      className="min-h-screen bg-slate-50 py-12 sm:py-16 px-4"
      data-testid="pionier-cockpit-page"
    >
      {/* Sprint 12+M5.17-q (22.07.2026) — Onboarding-Tour fuer Erst-User.
          Auto-open beim ersten Besuch, LocalStorage-persistiert, dismissible. */}
      <CockpitTour />
      <div className="max-w-4xl mx-auto">
        <PersonaHeader firstName={firstName} />

        {/* Sprint 12+M5.20 (27.07.2026) — Featured Muster-Card (ehemals
            Endgegner-Modus-Card): Direkt unter Hero, above the fold. Fuehrt
            Pioniere zur Vimeo-Indoctrination-Sequenz (/dashboard/pionier/videos). */}
        <div className="mt-8">
          <MusterCard user={user} />
        </div>

        {/* Sprint 12+M5.20 — Fokustyp-Referenz-Grid: visuelle Verankerung
            der 4 Fokustypen, aktiver Typ mit Gold-Outline hervorgehoben. */}
        <div className="mt-6">
          <FokustypReferenzGrid activeFokustyp={fokustyp} />
        </div>

        {/* Sprint 12+M5.6c (19.07.2026) — Neue 4-Section-Struktur:
            1. Was passiert jetzt?  (Konzept-Erklaerung)
            2. Was du jetzt tun kannst  (Status/Lead-in)
            3. Dein Steckbrief  (Kurzprofil oder Quick-Check-CTA)
            4. Bald verfuegbar  (Bullet-Liste statt Cards)
            Sprint 12+M5.7 (19.07.2026) — 2 zusaetzliche Sections:
            5. Du gestaltest mit  (Feedback-Form → /api/pionier/feedback)
            6. Was als Naechstes passiert  (Re-Engagement-Hinweis)
            V1.D (Launch-Consolidation, 30.08.2026) — BaldVerfuegbarSection
            ausgeblendet: Roadmap-/Coming-Soon-Content raus für alle Tiers
            (auch Pionier). Post-Launch wieder aktivieren. */}
        <div className="mt-10 space-y-8">
          <WasPassiertJetztSection />
          <WasDuJetztTunKannstSection hasFokustyp={hasFokustyp} />
          <SteckbriefSection fokustyp={fokustyp} />
          <AusfuehrlicherSteckbriefSection fokustyp={fokustyp} />
          {/* <BaldVerfuegbarSection /> — V1.D: ausgeblendet */}
          <FeedbackSection />
          <NaechsteSchritteSection />
        </div>

        {/* Sprint 12+I5 (04.07.2026) — Pionier-Bubble geschlossen:
            KEIN "Zurück zur Startseite"-Link mehr. Pioniere haben ihre
            eigene Journey und werden von / auf /dashboard/pionier redirected. */}
        <div className="mt-12 text-center">
          <p className="text-xs text-slate-400">
            Du kannst jederzeit auf die Bestätigungs-Mail antworten.
          </p>
        </div>
      </div>

      <PionierFeedbackIcon />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sprint 12+M5 (18.07.2026) — Tool-Session-Section (Cockpit)
// Zeigt Tages-Counter + Liste der letzten Sessions + "Neue Session"-Button.
// Datenquelle: useAuth().user (aus /api/auth/me erweitert mit sessions_today,
// daily_limit, latest_sessions, tier, is_pionier).
// ---------------------------------------------------------------------------

function ToolSessionSection({ user, onStartClick }) {
  const sessionsToday = Number(user?.sessions_today || 0);
  const dailyLimit = user?.daily_limit; // null = unbegrenzt
  const isPionier = !!user?.is_pionier;
  const latest = Array.isArray(user?.latest_sessions) ? user.latest_sessions : [];
  const limitReached = dailyLimit !== null && dailyLimit !== undefined && sessionsToday >= dailyLimit;

  return (
    <section
      className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8"
      data-testid="tool-session-section"
    >
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-5">
        <div>
          <h2
            className="text-xl sm:text-2xl font-extrabold text-slate-900 tracking-tight flex items-center gap-2"
            data-testid="tool-session-section-title"
          >
            <Sparkles className="w-5 h-5 text-[#4563BB]" />
            Dein Tool
          </h2>
          <p className="mt-1 text-sm text-slate-600">
            Beschreib eine Situation. Bekomm eine Fokustyp-Analyse und drei
            konkrete Gesprächs-Tipps.
          </p>
        </div>
        <UsageBadge
          sessionsToday={sessionsToday}
          dailyLimit={dailyLimit}
          isPionier={isPionier}
        />
      </div>

      <Button
        type="button"
        onClick={onStartClick}
        disabled={limitReached}
        className="w-full sm:w-auto h-12 rounded-xl bg-[#4563BB] hover:bg-[#3a54a3] text-white font-semibold text-base px-6"
        data-testid="tool-session-start-btn"
      >
        <Plus className="w-4 h-4 mr-2" />
        Neue Session starten
      </Button>

      {limitReached && (
        <p
          className="mt-3 text-sm text-amber-700"
          data-testid="tool-session-limit-hint"
        >
          Du hast heute alle {dailyLimit} Runs genutzt. Morgen früh stehen
          dir wieder {dailyLimit} zur Verfügung.
        </p>
      )}

      {latest.length > 0 && (
        <div className="mt-6 pt-5 border-t border-slate-100">
          <h3 className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-3">
            Deine letzten Sessions
          </h3>
          <ul
            className="space-y-2"
            data-testid="tool-session-history"
          >
            {latest.slice(0, 5).map((s) => (
              <SessionRow key={s.session_id} session={s} />
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

function UsageBadge({ sessionsToday, dailyLimit, isPionier }) {
  if (isPionier || dailyLimit === null || dailyLimit === undefined) {
    return (
      <div
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#4563BB]/10 text-[#4563BB] text-xs font-semibold"
        data-testid="tool-session-usage-badge"
      >
        <InfinityIcon className="w-3.5 h-3.5" />
        Pioniere testen unbegrenzt
      </div>
    );
  }
  return (
    <div
      className="text-sm font-semibold text-slate-700 px-3 py-1.5 rounded-full bg-slate-100"
      data-testid="tool-session-usage-badge"
    >
      Heute:{" "}
      <span className="text-[#4563BB]">{sessionsToday}</span>
      <span className="text-slate-400"> / {dailyLimit} Runs</span>
    </div>
  );
}

function SessionRow({ session }) {
  const dt = session.created_at ? new Date(session.created_at) : null;
  const dateStr = dt
    ? dt.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" }) +
      " · " +
      dt.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })
    : "";
  return (
    <li
      className="flex items-start gap-3 p-3 rounded-xl bg-slate-50 border border-slate-100"
      data-testid="tool-session-history-row"
    >
      <div className="flex-shrink-0 mt-1">
        <div className="w-8 h-8 rounded-full bg-white border border-slate-200 flex items-center justify-center">
          <Sparkles className="w-4 h-4 text-[#4563BB]" />
        </div>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          {session.fokustyp_detected && (
            <span
              className="text-xs font-bold uppercase tracking-wide text-[#4563BB]"
              data-testid="tool-session-history-fokustyp"
            >
              {session.fokustyp_detected}
            </span>
          )}
          {dateStr && (
            <span className="text-xs text-slate-500">{dateStr}</span>
          )}
        </div>
        <p className="mt-0.5 text-sm text-slate-700 line-clamp-2">
          {session.situation_preview || "—"}
        </p>
      </div>
    </li>
  );
}

// ---------------------------------------------------------------------------
// Persona-Header
// ---------------------------------------------------------------------------

function PersonaHeader({ firstName }) {
  return (
    <div className="text-center" data-testid="pionier-cockpit-header">
      <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#4563BB]/10 text-[#4563BB] text-xs font-semibold uppercase tracking-wide mb-4">
        <Sparkles className="w-3.5 h-3.5" />
        <span data-testid="pionier-cockpit-badge">Mein PocketCoach · Pionier</span>
      </div>
      <h1
        className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900"
        data-testid="pionier-cockpit-headline"
      >
        Hallo{firstName ? `, ${firstName}` : ""}.
      </h1>
      <p className="mt-4 text-base sm:text-lg text-slate-600 max-w-xl mx-auto">
        Erstelle in 2 Minuten dein Flex4Success-Profil und wirf einen
        Blick auf das, was kommt.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sprint 12+M5.6c (19.07.2026) — Sektionen "Was passiert jetzt?" (Fix 2) und
// "Was du jetzt tun kannst" (Fix 3). Konzept-Erklaerung + Status-Bestaetigung
// vor dem eigentlichen Kurzprofil.
// ---------------------------------------------------------------------------

function WasPassiertJetztSection() {
  return (
    <section
      className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sm:p-8"
      data-testid="pionier-was-passiert-jetzt-section"
    >
      <SectionEyebrow icon={<Sparkles className="w-5 h-5 text-[#4563BB]" />}>
        Was passiert jetzt?
      </SectionEyebrow>
      <p
        className="mt-3 text-sm sm:text-base text-slate-700 leading-relaxed"
        data-testid="pionier-was-passiert-jetzt-text"
      >
        Du bist einer der Ersten, die sehen, wie vier typische Reaktionsmuster
        zusammenwirken. Du gehörst zu den Ersten, die einen Einblick erhalten,
        wie vier typische Reaktionsmuster unsere Kommunikation und Beziehungen
        zu anderen Menschen beeinflussen.
      </p>
    </section>
  );
}

function WasDuJetztTunKannstSection({ hasFokustyp }) {
  // Sprint 12+M5.9-b (19.07.2026) — Andreas' finale Microcopy:
  //   pre-QC : „Was du tun kannst" + fette Sub-Headline + Rationale
  //   post-QC: „Was du erledigt hast" + Bestaetigungs-Text
  if (hasFokustyp) {
    return (
      <section
        className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sm:p-8"
        data-testid="pionier-was-du-tun-kannst-section"
      >
        <SectionEyebrow icon={<CheckCircle2 className="w-5 h-5 text-emerald-600" />}>
          Was du erledigt hast
        </SectionEyebrow>
        <p
          className="mt-3 text-sm sm:text-base text-slate-700 leading-relaxed"
          data-testid="pionier-was-du-tun-kannst-text"
        >
          Du hast dein Flex4Success-Profil angelegt. Hier siehst du das
          Ergebnis: Dein Fokustyp. Schau dir deinen Steckbrief an.
        </p>
      </section>
    );
  }

  return (
    <section
      className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sm:p-8"
      data-testid="pionier-was-du-tun-kannst-section"
    >
      <SectionEyebrow icon={<Sparkles className="w-5 h-5 text-[#4563BB]" />}>
        Was du tun kannst
      </SectionEyebrow>
      <p
        className="mt-3 text-base sm:text-lg font-bold text-slate-900 leading-snug"
        data-testid="pionier-was-du-tun-kannst-sub-headline"
      >
        Erstelle jetzt dein Flex4Success-Profil in nur zwei Minuten.
      </p>
      <p
        className="mt-2 text-sm sm:text-base text-slate-700 leading-relaxed"
        data-testid="pionier-was-du-tun-kannst-text"
      >
        Beantworte fünf Fragen und in zwei Minuten weißt du, welcher Fokustyp
        du bist. Damit hast du dein persönliches Profil angelegt.
      </p>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Sektion 3: Steckbrief (Kurzprofil bei bestehendem Fokustyp, sonst CTA)
// Sprint 12+M5.6c (19.07.2026) — Fix 5: "Dein Steckbrief"-Header ergaenzt.
// ---------------------------------------------------------------------------

function SteckbriefSection({ fokustyp }) {
  const navigate = useNavigate();
  const profile = FOKUSTYP_PROFILES[fokustyp];
  const t = FOCUS_TYPES[fokustyp];

  if (profile && t) {
    // Sprint 12+M5.4a (18.07.2026) — Fokustyp-Icon-Fix: statt Lucide-Icons
    // (Cat/Bird/Bug/Dog = Comic-Stil) nutzen wir die von Andreas hinterlegten
    // PNG-Icons aus /public/icons/icon-{katze|eule|biene|hund}.png. Der
    // Brand-Farb-Container (t.bg/t.border) bleibt fuer FT-Wiedererkennung.
    const glyphSrc = FOKUSTYP_GLYPHS[SLUG_EN_TO_DE[fokustyp]] || null;
    return (
      <section
        className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sm:p-8"
        data-testid="pionier-quickcheck-section"
      >
        <SectionEyebrow icon={<CheckCircle2 className="w-5 h-5 text-emerald-600" />}>
          Dein Steckbrief
        </SectionEyebrow>
        <p
          className="mt-2 text-sm text-slate-500"
          data-testid="pionier-steckbrief-subheader"
        >
          Du hast dein Flex4Success-Profil angelegt.
        </p>

        <div className="mt-6 flex items-start gap-4" data-testid="pionier-quickcheck-result">
          <div className={`w-16 h-16 rounded-2xl ${t.bg} ${t.border} border flex items-center justify-center flex-shrink-0 overflow-hidden p-1.5`}>
            {glyphSrc ? (
              <img
                src={glyphSrc}
                alt={profile.name}
                className="w-full h-full object-contain"
                draggable="false"
                data-testid="pionier-cockpit-fokustyp-glyph"
              />
            ) : null}
          </div>
          <div className="flex-1">
            <p className="text-sm text-slate-500">Dein Fokustyp</p>
            <h3
              className="text-2xl font-bold text-slate-900"
              data-testid="pionier-quickcheck-fokustyp-name"
            >
              {profile.name}
            </h3>
            <p className="mt-1 text-sm text-slate-600">{profile.tagline}</p>
          </div>
        </div>

        <div className="mt-6">
          <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wide">
            Erkennst du dich hier wieder?
          </h4>
          <ul
            className="mt-2 space-y-1.5"
            data-testid="pionier-quickcheck-erkennungsmerkmale"
          >
            {profile.erkennungsmerkmale.map((m, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                <CheckCircle2 className="w-4 h-4 mt-0.5 text-emerald-500 flex-shrink-0" />
                <span>{m}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="mt-4 grid sm:grid-cols-2 gap-3">
          <div className="p-3 rounded-xl bg-emerald-50 border border-emerald-100">
            <p className="text-xs font-bold text-emerald-800 uppercase tracking-wide">
              Deine Stärke
            </p>
            <p className="mt-1 text-sm text-emerald-900">{profile.staerke}</p>
          </div>
          <div className="p-3 rounded-xl bg-amber-50 border border-amber-100">
            <p className="text-xs font-bold text-amber-800 uppercase tracking-wide">
              Deine Schatten-Seite
            </p>
            <p className="mt-1 text-sm text-amber-900">{profile.schatten_seite}</p>
          </div>
        </div>

        {/* Sprint 12+M5.7 (19.07.2026) — Share-Button prominent nach dem
            Kurzprofil (statt am Ende versteckt). Nutzt reusable Component
            mit Andreas' Curiosity-Text-Template. */}
        <div
          className="mt-6 pt-5 border-t border-slate-100 flex flex-col sm:flex-row sm:items-center gap-3"
          data-testid="pionier-steckbrief-share-row"
        >
          <ShareFokustypButton
            fokustyp={fokustyp}
            testId="pionier-cockpit-share-button"
            variant="primary"
          />
          <p className="text-xs sm:text-sm text-slate-500 leading-snug">
            Teile deinen Fokustyp mit einer Person, die dich verstehen soll.
          </p>
        </div>

        <div className="mt-6">
          {/* Sprint 12+I5 (04.07.2026) — "Quick-Check erneut machen"-Button
              entfernt. Pioniere machen den Test nur einmal. */}
        </div>
      </section>
    );
  }

  // Empty-State
  return (
    <section
      className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sm:p-8"
      data-testid="pionier-quickcheck-section"
    >
      <SectionEyebrow icon={<Sparkles className="w-5 h-5 text-[#4563BB]" />}>
        Dein Steckbrief
      </SectionEyebrow>
      <div data-testid="pionier-quickcheck-empty">
        <p
          className="mt-4 text-base text-slate-700"
          data-testid="pionier-quickcheck-mini-framing"
        >
          In 2 Minuten weißt du, wie du kommunizierst — und welche Werkzeuge zu
          dir passen.
        </p>
        <div className="mt-5">
          <Button
            onClick={() => navigate("/pionier-quick-check")}
            className="bg-[#4563BB] hover:bg-[#3a54a2] h-11 text-base font-semibold"
            data-testid="pionier-quickcheck-start-btn"
          >
            Finde deinen Fokustyp · 2 Min
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Sektion 2: Flexpfad-Bibliothek
// ---------------------------------------------------------------------------

function BibliothekSection({ fokustyp }) {
  const [state, setState] = useState({ status: "idle", bausteine: [], error: "" });

  useEffect(() => {
    let cancelled = false;
    if (!fokustyp) {
      setState({ status: "no_fokustyp", bausteine: [], error: "" });
      return () => {};
    }
    (async () => {
      setState({ status: "loading", bausteine: [], error: "" });
      try {
        const { data } = await api.get(`/heart-piece/v3/${fokustyp}`);
        if (cancelled) return;
        const arr = Array.isArray(data?.bausteine) ? data.bausteine : [];
        setState({ status: "ready", bausteine: arr, error: "" });
      } catch (e) {
        if (cancelled) return;
        setState({
          status: "error",
          bausteine: [],
          error: "Bibliothek konnte nicht geladen werden. Bitte lade die Seite neu.",
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [fokustyp]);

  return (
    <section
      className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sm:p-8"
      data-testid="pionier-bibliothek-section"
    >
      <SectionEyebrow icon={<BookOpen className="w-5 h-5 text-amber-600" />}>
        2 · Flexpfad-Bibliothek
        <span className="ml-2 text-xs font-normal text-slate-500">
          Pionier-Preview
        </span>
      </SectionEyebrow>

      {state.status === "no_fokustyp" && (
        <div
          className="mt-4 p-4 rounded-xl bg-amber-50 border border-amber-100 text-sm text-amber-900"
          data-testid="pionier-bibliothek-no-fokustyp"
        >
          Mach zuerst den Quick-Check oben — dann laden wir deine persönliche
          Flexpfad-Bibliothek.
        </div>
      )}

      {state.status === "loading" && (
        <div className="mt-6 flex items-center justify-center py-8" data-testid="pionier-bibliothek-loading">
          <Loader2 className="w-6 h-6 text-slate-400 animate-spin" />
        </div>
      )}

      {state.status === "error" && (
        <div className="mt-4 p-4 rounded-xl bg-red-50 border border-red-100 text-sm text-red-800" data-testid="pionier-bibliothek-error">
          {state.error}
        </div>
      )}

      {state.status === "ready" && <BibliothekGrid bausteine={state.bausteine} />}
    </section>
  );
}

function BibliothekGrid({ bausteine }) {
  const items = useMemo(() => {
    const total = bausteine.length;
    const unlockedCount =
      total <= 1 ? total : Math.min(PREVIEW_UNLOCKED_MAX, Math.max(1, total - 1));
    return bausteine.map((b, i) => ({
      ...b,
      _idx: i,
      _unlocked: i < unlockedCount,
    }));
  }, [bausteine]);
  const unlockedCount = items.filter((b) => b._unlocked).length;
  const [openIdx, setOpenIdx] = useState(null);

  if (!items.length) {
    return (
      <div className="mt-4 text-sm text-slate-500" data-testid="pionier-bibliothek-empty">
        Keine Bausteine gefunden.
      </div>
    );
  }

  return (
    <>
      <p className="mt-3 text-sm text-slate-600">
        {unlockedCount} von {items.length} Bausteinen sind für dich als Pionier
        schon freigeschaltet. Die restlichen öffnen sich beim Hauptlaunch.
      </p>
      <div
        className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
        data-testid="pionier-bibliothek-grid"
      >
        {items.map((b) => (
          <BibliothekCard
            key={b._idx}
            item={b}
            expanded={openIdx === b._idx}
            onToggle={() =>
              b._unlocked && setOpenIdx((cur) => (cur === b._idx ? null : b._idx))
            }
          />
        ))}
      </div>
    </>
  );
}

function BibliothekCard({ item, expanded, onToggle }) {
  const nr = item.spielzug_nr ?? item._idx + 1;
  const title = item.titel || item.title || `Baustein ${nr}`;
  const teaser = item.teaser || item.summary || "";

  if (!item._unlocked) {
    return (
      <div
        className="rounded-2xl border border-slate-200 bg-slate-50 p-5 text-slate-500 cursor-not-allowed"
        data-testid={`pionier-bibliothek-card-locked-${nr}`}
      >
        <div className="flex items-center justify-between">
          <span className="text-xs font-semibold uppercase tracking-wide">
            Baustein {nr}
          </span>
          <Lock className="w-4 h-4 text-slate-400" />
        </div>
        <h4 className="mt-2 text-base font-bold text-slate-600 line-clamp-2">
          {title}
        </h4>
        <p className="mt-3 text-xs">Öffnet sich beim Hauptlaunch.</p>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={onToggle}
      className={`text-left rounded-2xl border p-5 transition-all ${
        expanded
          ? "border-[#4563BB] bg-[#4563BB]/5 ring-2 ring-[#4563BB]/20"
          : "border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm"
      }`}
      data-testid={`pionier-bibliothek-card-unlocked-${nr}`}
    >
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wide text-[#4563BB]">
          Baustein {nr}
        </span>
        <CheckCircle2 className="w-4 h-4 text-emerald-500" />
      </div>
      <h4 className="mt-2 text-base font-bold text-slate-900">{title}</h4>
      {teaser && (
        <p className="mt-2 text-sm text-slate-600 line-clamp-3">{teaser}</p>
      )}
      {expanded && (
        <div
          className="mt-4 pt-4 border-t border-slate-200 text-sm text-slate-700 space-y-2 whitespace-pre-line"
          data-testid={`pionier-bibliothek-card-content-${nr}`}
        >
          {renderBausteinContent(item)}
        </div>
      )}
    </button>
  );
}

function renderBausteinContent(item) {
  const parts = [];
  const push = (label, val) => {
    if (val && typeof val === "string" && val.trim()) {
      parts.push({ label, val: val.trim() });
    }
  };
  push("Was", item.was || item.beschreibung || item.description);
  push("Wie", item.wie || item.anwendung);
  push("Beispiel", item.beispiel || item.example);
  if (!parts.length && item.content) {
    parts.push({ label: "Inhalt", val: String(item.content) });
  }
  if (!parts.length) {
    return <span className="italic text-slate-500">Inhalt folgt.</span>;
  }
  return parts.map((p) => (
    <div key={p.label}>
      <span className="font-semibold text-slate-900">{p.label}: </span>
      {p.val}
    </div>
  ));
}

// ---------------------------------------------------------------------------
// Sektion "Dein ausführlicher Steckbrief" — Sprint 12+M5.9-b (19.07.2026)
// Lazy-fetch der v1.1-Inhalte (drei_ebenen, partner, sofort_tipps) via
// GET /api/fokustyp/{slug}/steckbrief. Rendert nichts, wenn kein Fokustyp.
// ---------------------------------------------------------------------------

function AusfuehrlicherSteckbriefSection({ fokustyp }) {
  const [data, setData] = useState(null);
  const [state, setState] = useState("idle"); // idle | loading | error

  useEffect(() => {
    if (!fokustyp) {
      setData(null);
      setState("idle");
      return;
    }
    const slug = SLUG_EN_TO_DE[fokustyp] || fokustyp;
    let cancelled = false;
    setState("loading");
    (async () => {
      try {
        const res = await api.get(`/fokustyp/${encodeURIComponent(slug)}/steckbrief`);
        if (cancelled) return;
        setData(res?.data || null);
        setState("idle");
      } catch {
        if (cancelled) return;
        setState("error");
      }
    })();
    return () => { cancelled = true; };
  }, [fokustyp]);

  if (!fokustyp) return null;

  if (state === "loading") {
    return (
      <section
        className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sm:p-8"
        data-testid="pionier-steckbrief-detail-section"
      >
        <div className="flex items-center gap-2 text-slate-500 text-sm">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span>Steckbrief wird geladen …</span>
        </div>
      </section>
    );
  }

  if (state === "error" || !data) {
    return null; // still fail — kein Grund den Cockpit-Flow zu blocken
  }

  const drei = data.drei_ebenen || {};
  const tipps = Array.isArray(data.tipps) ? data.tipps : [];

  return (
    <section
      className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sm:p-8"
      data-testid="pionier-steckbrief-detail-section"
    >
      <SectionEyebrow icon={<BookOpen className="w-5 h-5 text-[#4563BB]" />}>
        Dein ausführlicher Steckbrief
      </SectionEyebrow>

      {/* Drei-Ebenen-Block */}
      {(drei.inhalt_7_prozent || drei.tonalitaet_38_prozent || drei.koerpersprache_55_prozent) && (
        <div className="mt-4" data-testid="pionier-steckbrief-drei-ebenen">
          {data.drei_ebenen_kontext && (
            <p className="text-sm text-slate-700 leading-relaxed">
              {data.drei_ebenen_kontext}
            </p>
          )}
          <div className="mt-4 space-y-3">
            {drei.inhalt_7_prozent && (
              <DreiEbeneRow label="Inhalt" text={drei.inhalt_7_prozent} />
            )}
            {drei.tonalitaet_38_prozent && (
              <DreiEbeneRow label="Tonalität" text={drei.tonalitaet_38_prozent} />
            )}
            {drei.koerpersprache_55_prozent && (
              <DreiEbeneRow label="Körpersprache" text={drei.koerpersprache_55_prozent} />
            )}
          </div>
        </div>
      )}

      {/* Sprint 12+M5.12 (19.07.2026) — Partner-Fokustyp-Sektion entfernt.
          Grund: Frage „Wer passt zu dir, {Fokustyp}?" ist logisch falsch
          gestellt (fragt den eigenen Fokustyp, nicht welcher komplementäre
          Typ passt). Post-Launch (Sprint M6+) mit korrekter Logik wieder
          einbauen — als „Welcher Fokustyp fordert dich am meisten heraus?"
          oder „Welcher Fokustyp passt zu MIR?". */}

      {/* Sofort-Tipps: cross-typ-Kommunikation */}
      {tipps.length > 0 && (
        <div
          className="mt-6 pt-5 border-t border-slate-100"
          data-testid="pionier-steckbrief-tipps"
        >
          <div className="flex items-center gap-2 mb-3">
            <Eye className="w-4 h-4 text-[#4563BB]" />
            <h4 className="text-sm font-bold text-slate-900">
              So erreichst du die anderen Fokustypen
            </h4>
          </div>
          <div className="space-y-4">
            {tipps.map((t, i) => (
              <div
                key={t.ziel_fokustyp || i}
                className="p-4 rounded-xl bg-slate-50 border border-slate-100"
                data-testid={`pionier-steckbrief-tipp-${t.ziel_fokustyp}`}
              >
                <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">
                  Ziel: {t.ziel_name || t.ziel_fokustyp}
                </p>
                <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">
                  {t.text}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

function DreiEbeneRow({ label, text }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-4">
      <span className="text-xs font-bold uppercase tracking-wider text-[#4563BB] sm:w-40 sm:flex-shrink-0">
        {label}
      </span>
      <p className="text-sm text-slate-700 leading-relaxed flex-1">{text}</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sektion 4: "Bald verfügbar" — Sprint 12+M5.6c (19.07.2026)
// Fix 1: Kompakte Bullet-Liste statt 4 visuell schwerer Feature-Cards.
// Fix 4: "Katze, Eule, Biene, Hund"-Aufzaehlung als <strong> fett gerendert.
// ---------------------------------------------------------------------------

function BaldVerfuegbarSection() {
  return (
    <section
      className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sm:p-8"
      data-testid="pionier-bald-verfuegbar-section"
    >
      <SectionEyebrow icon={<Sparkles className="w-5 h-5 text-[#4563BB]" />}>
        Bald verfügbar
      </SectionEyebrow>
      <p className="mt-3 text-sm sm:text-base text-slate-700 leading-relaxed">
        Vier Reaktionsmuster —{" "}
        <strong data-testid="pionier-fokustypen-bold">
          Katze, Eule, Biene, Hund
        </strong>{" "}
        — bilden das Fundament von Flex4Success. Als Pionier erhältst du als
        Erste:r Zugriff auf:
      </p>
      <ul
        className="mt-4 space-y-2"
        data-testid="pionier-bald-verfuegbar-list"
      >
        {BALD_VERFUEGBAR_ITEMS.map((item, i) => (
          <li
            key={i}
            className="flex items-start gap-2.5 text-sm sm:text-base text-slate-700"
            data-testid={`pionier-bald-verfuegbar-item-${i}`}
          >
            <CheckCircle2 className="w-4 h-4 mt-1 text-[#4563BB] flex-shrink-0" />
            <span>{item}</span>
          </li>
        ))}
      </ul>
      <p
        className="mt-5 text-sm text-slate-500 leading-relaxed"
        data-testid="pionier-bald-verfuegbar-hint"
      >
        Du gehörst zu den Ersten, die diese Funktionen als Pionier erhalten.
        Sobald sie fertig sind, wirst du benachrichtigt.
      </p>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Sprint 12+M5.7 (19.07.2026) — Sektion "Du gestaltest mit" (Feedback-Form).
// POSTet an /api/pionier/feedback (neue Collection `pionier_feedback`).
// Inline-Form statt Modal, damit User es nicht "wegklicken" koennen — im
// Sinne von Russell-Brunson: mach den Weg zum Mitgestalten so nah wie moeglich.
// ---------------------------------------------------------------------------

function FeedbackSection() {
  const [text, setText] = useState("");
  const [state, setState] = useState("idle"); // idle | submitting | success | error
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim()) {
      setError("Ein paar Worte reichen. Was hast du im Kopf?");
      setState("error");
      return;
    }
    setState("submitting");
    setError("");
    try {
      await api.post("/pionier/feedback", { feedback_text: text.trim() });
      setState("success");
      setText("");
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          "Feedback konnte nicht gesendet werden. Versuch's gleich noch mal."
      );
      setState("error");
    }
  };

  return (
    <section
      className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sm:p-8"
      data-testid="pionier-feedback-section"
    >
      <SectionEyebrow icon={<MessageSquare className="w-5 h-5 text-[#4563BB]" />}>
        Du gestaltest mit
      </SectionEyebrow>
      <p className="mt-3 text-sm sm:text-base text-slate-700 leading-relaxed">
        Was wünschst du dir als Pionier? Was fehlt dir? Was funktioniert?
      </p>

      {state === "success" ? (
        <div
          className="mt-5 p-4 rounded-xl bg-emerald-50 border border-emerald-100 flex items-start gap-3"
          data-testid="pionier-feedback-success"
        >
          <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-emerald-900">
              Danke — dein Feedback ist bei uns.
            </p>
            <p className="mt-1 text-xs text-emerald-800">
              Wir lesen jede Nachricht persönlich.
            </p>
            <button
              type="button"
              onClick={() => setState("idle")}
              className="mt-3 text-xs font-semibold text-emerald-700 hover:text-emerald-900 underline"
              data-testid="pionier-feedback-again-btn"
            >
              Noch etwas schreiben
            </button>
          </div>
        </div>
      ) : (
        <form
          onSubmit={submit}
          noValidate
          className="mt-4"
          data-testid="pionier-feedback-form"
        >
          <Textarea
            value={text}
            onChange={(e) => {
              setText(e.target.value);
              if (state === "error") setState("idle");
            }}
            placeholder="Schreib uns kurz — 2-3 Sätze reichen."
            className="min-h-[110px] text-sm"
            disabled={state === "submitting"}
            data-testid="pionier-feedback-textarea"
          />
          {state === "error" && (
            <p
              className="mt-2 text-xs text-red-600"
              data-testid="pionier-feedback-error"
            >
              {error}
            </p>
          )}
          <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
            <p className="text-xs text-slate-500">
              Deine Nachricht geht direkt an das Flex4Success-Team.
            </p>
            <Button
              type="submit"
              disabled={state === "submitting" || !text.trim()}
              className="bg-[#4563BB] hover:bg-[#3a54a2] h-10 text-sm font-semibold px-5"
              data-testid="pionier-feedback-submit-btn"
            >
              {state === "submitting" ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Wird gesendet …
                </>
              ) : (
                <>
                  Absenden <Send className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          </div>
        </form>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Sprint 12+M5.7 (19.07.2026) — Sektion "Was als Naechstes passiert".
// Re-Engagement-Trigger: klarer Hinweis, dass Nachrichten kommen — ohne
// konkrete Datums-Versprechen (bewusst vage, damit wir keine Deadline reissen).
// ---------------------------------------------------------------------------

function NaechsteSchritteSection() {
  return (
    <section
      className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sm:p-8"
      data-testid="pionier-naechste-schritte-section"
    >
      <SectionEyebrow icon={<Clock className="w-5 h-5 text-[#4563BB]" />}>
        Was als Nächstes passiert
      </SectionEyebrow>
      <p
        className="mt-3 text-sm sm:text-base text-slate-700 leading-relaxed"
        data-testid="pionier-naechste-schritte-text"
      >
        Du erhältst eine Nachricht von mir, sobald die nächsten Funktionen
        live gehen. Bis dahin: Du hast alles, was du jetzt brauchst.
      </p>
      <p
        className="mt-2 text-sm text-slate-500 leading-relaxed"
        data-testid="pionier-naechste-schritte-hint"
      >
        Schau in den nächsten Tagen wieder rein — dein PocketCoach wächst mit.
      </p>
    </section>
  );
}

function SectionEyebrow({ icon, children }) {
  return (
    <div className="flex items-center gap-2 text-lg font-bold text-slate-900">
      {icon}
      <span>{children}</span>
    </div>
  );
}
